import React, { useEffect, useRef, useState } from "react"
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  CircularProgress,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteForeverIcon from "@mui/icons-material/DeleteForever"
import useImage from "../../../hooks/useImage"
import useLang from "../../../hooks/useLang"

export interface MinTypeFile {
  id?: number
  name: string
  url: string
}

interface Props {
  onFilesUpload: (files: File[]) => void
  onClearUpload: (file: MinTypeFile) => void
  label: string
  required?: boolean
  id: string
  defaultUrls?: MinTypeFile[]
}

const CustomUploadFiles = ({
  onFilesUpload,
  onClearUpload,
  label,
  required,
  id,
  defaultUrls = [],
}: Props) => {
  const [dragOver, setDragOver] = useState(false)
  const [loading, setLoading] = useState(false)
  const [imagePreviews, setImagePreviews] = useState<MinTypeFile[]>(defaultUrls)
  const { getUri } = useImage()
  const refInput = useRef<HTMLInputElement>(null)
  const { getText } = useLang()

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(false)
  }

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragOver(false)
    const files = event.dataTransfer.files
    if (files) {
      handleFilesChange(files)
    }
  }

  const handleFilesChange = (files: FileList) => {
    const validFiles: File[] = []
    const maxSize = 1 // MB

    Array.from(files).forEach((file) => {
      const fileSize = file.size / 1024 / 1024
      if (fileSize < maxSize) {
        validFiles.push(file)
      } else {
        alert(`Le fichier "${file.name}" dépasse la limite de ${maxSize}MB`)
      }
    })

    if (validFiles.length > 0) {
      setLoading(true)
      onFilesUpload(validFiles)

      const readers = validFiles.map((file) => {
        return new Promise<MinTypeFile>((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => {
            resolve({
              name: file.name,
              url: reader.result as string,
            })
          }
          reader.readAsDataURL(file)
        })
      })

      Promise.all(readers).then((newPreviews) => {
        setImagePreviews((prev) => [...prev, ...newPreviews])
        setLoading(false)
      })
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files) {
      handleFilesChange(files)
    }
  }

  const handleClearImage = (file: MinTypeFile) => {
    setImagePreviews((prev) => prev.filter((f) => f.name !== file.name))
    if (refInput.current) refInput.current.value = ""
    onClearUpload(file)
  }

  useEffect(() => {
    if (defaultUrls.length > 0) {
      setImagePreviews(defaultUrls)
    }
  }, [defaultUrls])

  return (
    <>
      <Box component="label" htmlFor={id}>
        {label} {required ? "*" : ""}
      </Box>
      <Grid container alignItems="center" style={{ paddingTop: 10 }}>
        <Grid item xs={12} lg={imagePreviews.length > 0 ? 3 : 12}>
          <Paper
            variant="outlined"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: dragOver ? "2px dashed #000" : "2px dashed #aaa",
              padding: imagePreviews.length > 0 ? 15 : 29,
              textAlign: "center",
              cursor: "pointer",
              background: dragOver ? "#eee" : "#fafafa",
              position: "relative",
            }}
          >
            <input
              accept="image/*"
              style={{ display: "none" }}
              id={id}
              multiple
              type="file"
              required={required}
              onChange={handleChange}
              ref={refInput}
            />
            <label htmlFor={id}>
              <Box display="flex" flexDirection="column" alignItems="center">
                <IconButton
                  color="primary"
                  aria-label="upload"
                  component="span"
                >
                  <CloudUploadIcon style={{ fontSize: 50 }} />
                </IconButton>
                <Typography style={{ color: "black" }}>
                  {getText("Faites glisser et déposez les images")}
                </Typography>
              </Box>
            </label>
            {loading && (
              <CircularProgress
                size={24}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  marginTop: "-12px",
                  marginLeft: "-12px",
                }}
              />
            )}
          </Paper>
        </Grid>

        {imagePreviews.map((preview, index) => (
          <Grid item xs={3} key={index} ml={1} mt={1} position="relative">
            <IconButton
              onClick={() => handleClearImage(preview)}
              color="error"
              aria-label="delete picture"
              style={{
                backgroundColor: "white",
                position: "absolute",
                top: 4,
                right: 4,
                zIndex: 1,
              }}
            >
              <DeleteForeverIcon style={{ fontSize: 20 }} />
            </IconButton>
            <Box
              component="img"
              src={getUri(preview.url)}
              alt="Preview"
              sx={{ width: "100%", height: 141, borderRadius: 2 }}
            />
          </Grid>
        ))}
      </Grid>
    </>
  )
}

export default CustomUploadFiles
